/** What a finished game is worth.
 *
 * The arithmetic lives beside the screen that prints it rather than in the
 * host, because the end screen has to be able to show a player *why* he scored
 * what he did, and a formula whose only implementation is inside a storage
 * module cannot be broken out into a breakdown without being written twice.
 *
 * It is pure and it takes the kingdom value as a number. Computing that value
 * is the kernel's job (`kingdomScore`, core/adventure/turn.ts) and the shell
 * may not call a rule module (I10), so the host reads it and passes it in.
 *
 * Basis points throughout, and one rounding at the end - the same discipline
 * the kernel keeps, for the same reason: two builds that round differently
 * would disagree about a table that is supposed to be comparable.
 */

export type ScoreOutcome = "victory" | "defeat" | "draw";

/**
 * How much a difficulty level is worth.
 *
 * The ladder is the AI's own, so the level that makes the computer search
 * deeper is the level that pays. Impossible also starts the human with nothing,
 * which the kingdom value will have felt all game - the multiplier is on top of
 * that, not instead of it.
 */
export const DIFFICULTY_MULTIPLIER_BP: Readonly<Record<string, number>> = Object.freeze({
  easy: 7000, normal: 10000, hard: 13000, impossible: 16000,
});

/**
 * The day a game is expected to take.
 *
 * Finishing on day one pays double and finishing on par pays face value.
 * Finishing *after* par pays face value too rather than less: a long war that
 * was won is still a war that was won, and a penalty that kept biting would
 * make a hard-fought hundred-and-eighty-day victory score under a walkover.
 */
export const PAR_DAY = 120;

/** A loss is recorded, at a fraction: the table is a record, not a trophy. */
export const DEFEAT_SHARE_BP = 4000;
/** A game the stalemate breaker adjudicated, or one nobody survived. */
export const DRAW_SHARE_BP = 7000;

export const BP = 10000;

export interface ScoreInput {
  /** The kernel's `kingdomScore` for the player being scored. */
  readonly kingdom: number;
  /** Absolute day the game ended on. */
  readonly day: number;
  /** A key of `DIFFICULTY_MULTIPLIER_BP`; anything else scores as normal. */
  readonly difficulty: string;
  readonly outcome: ScoreOutcome;
}

export interface ScoreBreakdown {
  readonly kingdom: number;
  readonly speedBp: number;
  readonly difficultyBp: number;
  readonly outcomeBp: number;
  readonly total: number;
}

export const speedBonusBp = (day: number): number =>
  (day >= PAR_DAY ? BP : BP + Math.round(((PAR_DAY - day) * BP) / PAR_DAY));

export const outcomeShareBp = (outcome: ScoreOutcome): number =>
  (outcome === "victory" ? BP : outcome === "draw" ? DRAW_SHARE_BP : DEFEAT_SHARE_BP);

/**
 * `kingdom x speed x difficulty x outcome`.
 *
 * Worked example, so the table is predictable: a Normal game won on day 60 with
 * a kingdom worth 9000 scores 9000 x 1.5 x 1.0 x 1.0 = 13500.
 */
export function scoreBreakdown(input: ScoreInput): ScoreBreakdown {
  const speedBp = speedBonusBp(input.day);
  const difficultyBp = DIFFICULTY_MULTIPLIER_BP[input.difficulty] ?? BP;
  const outcomeBp = outcomeShareBp(input.outcome);
  const kingdom = input.kingdom < 0 ? 0 : input.kingdom;
  const total = Math.round((kingdom * speedBp * difficultyBp * outcomeBp) / (BP * BP * BP));
  return { kingdom, speedBp, difficultyBp, outcomeBp, total: total < 0 ? 0 : total };
}

export const finalScore = (input: ScoreInput): number => scoreBreakdown(input).total;
